import AppEvents from 'lib/AppEvents';
import moment from 'moment';
import ApiService from "services/ApiService";
import CurrencyService from 'services/CurrencyService';
import DataService from "services/data/DataService";
import EventService from 'services/EventService';
import UserService from 'services/UserService';
import IDbOrCacheStore from 'stores/IDbOrCacheStore';
import DateArrays from "utils/DateArrays";
import { DATE_FORMAT, today } from 'utils/DateUtils';
import { findError } from 'utils/errorUtils';

// TODO: this should be removed, in favor of new data system.

const DAY_FORMAT = 'YYYY-MM-DD';

class AdspendService {

    private store: IDbOrCacheStore;

    constructor() {
        this.store = new IDbOrCacheStore('SPND_');
        EventService.subscribe(AppEvents.LOGIN, async () => {
            await this.store.clear();
        });
        EventService.subscribe(AppEvents.LOGOUT, async () => {
            await this.store.clear();
        });
    }

    public clearData = async () => {
        await this.store.clear();
    }

    // Local method for pulling Facebook daily adspend
    private async getFacebookDailyData(accountId: string, from: string, to: string, forceTrail: number = 0) {
        let r = await ApiService.get(`/account-data/${accountId}/facebook/spend`, {
            params: { from, to, forceTrail }
        });
        if (!r.success) {
            throw new Error("Error retrieving facebook spend data for account: " + accountId + ", error: " + findError(r.error));
        }
        return r.result;
    }

    // Public method to update the Facebook Adspend data
    public async updateAdspendFacebookData(user, updateCallback ) {
        let accountId = user.activeAccount._id;
        const _today = today().format(DATE_FORMAT);
        const _todayLocal = moment().format(DATE_FORMAT);
        //const _now = today().format(DATE_FORMAT + HOURLY_FORMAT);
        let lastPulled = await this.store.get('lastPulledFacebook');
        let rates = await CurrencyService.getExchangeRates();
        let fbAdspend = undefined;

        // if we pulled today and today is both today relative to store, and also user's local:
        // (this is to try to minimize a bug with accounts not seeing updated MER for some reason).
        if (lastPulled == _today && lastPulled == _todayLocal) {
            fbAdspend = await DataService.getDataSource('adspend-facebook');
        }

        // If cached pull day isn't today, then update the DataService datasource
        if (!(lastPulled == _today && lastPulled == _todayLocal) || !fbAdspend) {
            // First, clear local datasource cache
            await DataService.saveDataSource('adspend-facebook', undefined);

            // Create a forceTrail of 14 days (meaning back-end will always refresh the last 14 days with newly pulled FB data)
            const forceTrail = 14;

            // Format the from and to parameters
            let from = moment.min(Object.values(DateArrays).map(da => moment(da()[0]))).format(DAY_FORMAT);
            let to = today().format(DAY_FORMAT);

            try {
                // Pull the data
                fbAdspend = {};
                let fbDayData: any = await this.getFacebookDailyData(accountId, from, to, forceTrail);

                if (fbDayData.taskId) {
                    if (await ApiService.pollForTaskCompletion(fbDayData.taskId)) {
                        fbDayData = await this.getFacebookDailyData(accountId, from, to, forceTrail);
                    }
                }

                // Format the data into the data object
                if (fbDayData && fbDayData.length) {
                    fbDayData.forEach(fbData => {
                        let providerDay = fbData.providers.find(p => p.name == 'Facebook');
                        fbAdspend[fbData.day] = {
                            spend: providerDay.data.spend,
                            isComplete: providerDay.isComplete ? true : false
                        }
                    });
                }

                // Convert FB spend data to current account currency:
                const { needsConversion, convertFrom, convertTo } = await UserService.getCurrencyConversionStatus(user);
                if (needsConversion) {
                    Object.keys(fbAdspend).forEach(day => {
                        fbAdspend[day].spend = CurrencyService.convertCurrency(fbAdspend[day].spend, convertFrom, convertTo, rates);
                    });
                }

                await DataService.saveDataSource('adspend-facebook', fbAdspend);
                await this.store.set('lastPulledFacebook', _today);

            } catch(error) {
                console.log('Error retrieving adspend: ', error, 'for user:', user);
                await DataService.saveDataSource('adspend-facebook', { error: 'There was an error retrieving your adspend data at this time.'});
                throw error;
            }
        }

        return fbAdspend;
    }

}

export default new AdspendService();